import { useMemo } from "react";
import * as Yup from "yup";
import { ContactFormSchema, contactFormInitialValues, IContactForm, onSubmitContactForm } from "@/pages/checkout/components/ContactForm";
import { LocationFormSchema, locationFormInitialValues, ILocationForm, onSubmitLocationForm } from "@/pages/checkout/components/LocationForm";
import { PaymentFormSchema, paymentFormInitialValues, IPaymentForm } from "@/pages/checkout/components/PaymentForm";
import { CheckoutSharedProps } from "@/pages/checkout/CheckoutPage";
import { useAppSelector } from "@/hooks";
import { branchFormInitialValues, BranchFormSchema, IBranchForm } from "@/pages/checkout/components/BranchForm";
import { IScheduleTimeForm, scheduleTimeFormInitialValues, ScheduleTimeFormSchema } from "../components/ScheduleTimeForm";
import { IconSvgElement } from "@hugeicons/react";
import { Call02Icon, DateTimeIcon, Location06Icon, PaymentSuccess02Icon, StoreLocation01Icon, TextCheckIcon } from "@hugeicons/core-free-icons";

export type ICheckout = IContactForm & ILocationForm & IPaymentForm & IBranchForm & IScheduleTimeForm;
export interface Step {
  id: string;
  icon?: IconSvgElement;
  title: string;
  schema?: Yup.AnyObject;
  initialValues?: IContactForm | ILocationForm | IPaymentForm | IBranchForm | IScheduleTimeForm;
  onSubmit?: (values: ICheckout, sharedProps: CheckoutSharedProps) => void;
}

const CONTACT_STEP: Step = {
  id: "contact",
  icon: Call02Icon,
  title: "Datos de contacto",
  schema: ContactFormSchema,
  initialValues: contactFormInitialValues,
  onSubmit: onSubmitContactForm,
};

const BRANCH_STEP: Step = {
  id: "branch",
  icon: StoreLocation01Icon,
  title: "Sucursal",
  schema: BranchFormSchema,
  initialValues: branchFormInitialValues,
};
const TIME_STEP: Step = {
  id: "time",
  icon: DateTimeIcon,
  title: "Horario",
  schema: ScheduleTimeFormSchema,
  initialValues: scheduleTimeFormInitialValues,
};

const LOCATION_STEP: Step = {
  id: "location",
  icon: Location06Icon,
  title: "Dirección",
  schema: LocationFormSchema,
  initialValues: locationFormInitialValues,
  onSubmit: onSubmitLocationForm,
};

const PAYMENT_STEP: Step = {
  id: "payment",
  icon: PaymentSuccess02Icon,
  title: "Métodos de Pago",
  schema: PaymentFormSchema,
  initialValues: paymentFormInitialValues,
};

const CONFIRMATION_STEP: Step = {
  id: "confirmation",
  icon: TextCheckIcon,
  title: "Confirmación",
};

const useCheckoutSteps = () => {
  const selectedDineMode = useAppSelector((store) => store.order.flow);

  const steps = useMemo(() => {
    switch (selectedDineMode) {
      case "delivery":
        return [CONTACT_STEP, LOCATION_STEP, PAYMENT_STEP, CONFIRMATION_STEP];
      case "pick-up":
        return [CONTACT_STEP, TIME_STEP, CONFIRMATION_STEP];
      case "reservations":
        return [CONTACT_STEP, CONFIRMATION_STEP];
      case "dine-in":
        return [CONTACT_STEP, PAYMENT_STEP, CONFIRMATION_STEP];
      default:
        return [CONTACT_STEP, LOCATION_STEP, PAYMENT_STEP, CONFIRMATION_STEP];
    }
  }, [selectedDineMode]);

  const stepId = location.pathname.split("/").pop();

  const activeStep = useMemo(() => {
    const step = steps.find((step) => step.id === stepId);
    return step ?? null;
  }, [steps, stepId]);

  const isFirstStep = useMemo(() => {
    const index = steps.findIndex((step) => step.id === stepId);
    return index === 0;
  }, [steps, stepId]);

  const isLastStep = useMemo(() => {
    const index = steps.findIndex((step) => step.id === stepId);
    return index === steps.length - 1;
  }, [steps, stepId]);

  const previousStep = useMemo(() => {
    const index = steps.findIndex((step) => step.id === stepId);
    if (index === 0) return steps[0];
    return steps[index - 1];
  }, [steps, stepId]);

  const stepsProgress = useMemo(() => {
    const index = steps.findIndex((step) => step.id === stepId) + 1;
    return (index * 100) / steps.length;
  }, [steps, stepId]);

  const nextStep = useMemo(() => {
    const index = steps.findIndex((step) => step.id === stepId);
    if (index === steps.length - 1) return steps[steps.length - 1];
    return steps[index + 1];
  }, [steps, stepId]);

  const initialValues = useMemo(() => {
    if (!steps.length) return {};

    return steps.reduce(
      (acc, curr) => ({
        ...acc,
        ...(curr?.initialValues ?? {}),
      }),
      {} as ICheckout
    );
  }, [steps]);

  const validationSchema = useMemo(() => {
    if (isLastStep) {
      const draft = [...steps];
      draft.pop();

      return draft.reduce((acc, curr) => {
        if (curr.schema) {
          return acc.concat(curr.schema);
        }
        return acc;
      }, Yup.object().shape({}));
    }

    const step = steps.find((step) => step.id === stepId);
    return step?.schema;
  }, [isLastStep, steps, stepId]);

  return {
    steps,
    stepId,
    activeStep,
    isFirstStep,
    isLastStep,
    previousStep,
    nextStep,
    initialValues,
    validationSchema,
    stepsProgress,
  };
};

export default useCheckoutSteps;
